import { useGetRetailerList } from '@/hooks/useProductQuery';
import { LocalStorageUtility } from '@/utilities/LocalStorageUtility';
import { Autocomplete, TextField } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const DefaultRetailerStorageKey = 'defaultRetailerId';

export const withRetailerIdParam = (route: string, retailerId: string) => (
  `${route}?${new URLSearchParams({ retailerId })}`
);

export const useDefaultRetailerId = () => {
  const [defaultRetailerId, setDefaultRetailerIdState] = useState<string | null>(
    () => LocalStorageUtility.getItem(DefaultRetailerStorageKey),
  );

  const setDefaultRetailerId = (retailerId: string | null) => {
    if (retailerId) {
      LocalStorageUtility.set(DefaultRetailerStorageKey, retailerId);
    } else {
      LocalStorageUtility.remove(DefaultRetailerStorageKey);
    }
    setDefaultRetailerIdState(retailerId);
  };

  return { defaultRetailerId, setDefaultRetailerId };
};

interface DefaultRetailerSelectorProps {
  onChange: (retailerId: string | null) => void,
  value: string | null,
}

export const DefaultRetailerSelector = ({ onChange, value }: DefaultRetailerSelectorProps) => {
  const { t } = useTranslation();
  const { retailerList } = useGetRetailerList();
  const retailers = [...(retailerList ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  const selectedRetailer = retailers.find((retailer) => retailer.id === value) ?? null;

  return (
    <Autocomplete
      data-testid="default-retailer-selector"
      getOptionLabel={(retailer) => retailer.name}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onChange={(_, retailer) => onChange(retailer?.id ?? null)}
      options={retailers}
      renderInput={(params) => (
        <TextField {...params} label={t($ => $.shared.defaultRetailer.label)} />
      )}
      size="small"
      sx={{ width: 240 }}
      value={selectedRetailer}
    />
  );
};
